import React, { useContext, useState, useEffect } from 'react'
import moment from 'moment'
import { Link, Navigate, useNavigate } from 'react-router-dom'

import API from '../../../services/API'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import { GlobalContext } from '../../../App'

import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ClearIcon from '@mui/icons-material/Clear'
import FormControlLabel from '@mui/material/FormControlLabel'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import ButtonGroup from '@mui/material/ButtonGroup'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'

import SearchIcon from '@mui/icons-material/Search'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import EmailIcon from '@mui/icons-material/Email'
import BigModal from '../../../components/Modals/BigModal'
import OutboundSMS from '../../system/OutboundSMS'
import InstallMobileIcon from '@mui/icons-material/InstallMobile'
import DoneIcon from '@mui/icons-material/Done'
import DoDisturbIcon from '@mui/icons-material/DoDisturb'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import ClipboardIcon from '@mui/icons-material/CopyAll'

const Employees = () => {
  const [dataLoading, setDataLoading] = useState(true)
  const [employees, setEmployees] = useState([])
  const [includeArchived, setIncludeArchived] = useState(false)
  const [selectedEmployeesModel, setSelectedEmployeesModel] = useState([])
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [openSMS, setOpenSMS] = useState(false)
  const navigate = useNavigate()
  const { setToast } = useContext(GlobalContext)

  const selectedEmployees = employees.filter((row) =>
    selectedEmployeesModel.includes(row._id),
  )

  const fetchEmployees = async () => {
    setDataLoading(true)
    try {
      const res = await API.get('/employees?includephoto=true', {
        params: {
          includeArchived,
          search,
        },
      })
      setEmployees(res.data)
    } catch (err) {
      console.error(err)
    } finally {
      setDataLoading(false)
    }
  }

  useEffect(() => {
    const debounce = setTimeout(() => {
      setSearch(searchInput)
    }, 400)
    return () => clearTimeout(debounce)
  }, [searchInput])

  useEffect(() => {
    fetchEmployees()
  }, [includeArchived, search])

  const CustomToolbar = ({}) => {
    return (
      <Stack spacing={2} mb={2}>
        {/* Actions Row */}
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', lg: 'center' }}
          spacing={2}
        >
          {/* Action Buttons */}
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              variant="contained"
              // color="third"
              startIcon={<PersonAddIcon />}
              component={Link}
              to="/employees/new"
              size="small"
            >
              New
            </Button>

            <ButtonGroup>
              <Tooltip title="Email selected employees">
                <>
                  <Button
                    disabled={selectedEmployees.length === 0}
                    variant="contained"
                    color="secondary"
                    size="small"
                    startIcon={<EmailIcon />}
                    onClick={() => {
                      const mailto = `mailto:?bcc=${selectedEmployees
                        .map((x) => x.email)
                        .join(';')}`
                      window.open(mailto)
                    }}
                  >
                    Email
                  </Button>
                </>
              </Tooltip>

              <Tooltip title="Copy email list to clipboard">
                <>
                  <Button
                    disabled={selectedEmployees.length === 0}
                    variant="contained"
                    color="secondary"
                    size="small"
                    onClick={() => {
                      const emails = selectedEmployees
                        .map((x) => x.email)
                        .join(';')
                      navigator.clipboard.writeText(emails).then(() => {
                        setToast({ message: 'Emails copied to clipboard' })
                      })
                    }}
                  >
                    <ClipboardIcon />
                  </Button>
                </>
              </Tooltip>
            </ButtonGroup>

            <Tooltip title="Send SMS to selected employees">
              <>
                <Button
                  disabled={selectedEmployees.length === 0}
                  variant="contained"
                  color="secondary"
                  size="small"
                  startIcon={<InstallMobileIcon />}
                  onClick={() => setOpenSMS(true)}
                >
                  SMS
                </Button>
              </>
            </Tooltip>
          </Stack>

          {/* Archived Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
                color="success"
                disabled={dataLoading}
              />
            }
            label="Include Archived"
          />
        </Stack>

        {/* SMS Modal */}
        <BigModal open={openSMS} onClose={() => setOpenSMS(false)}>
          <OutboundSMS
            passedEmployees={selectedEmployees.map((x) => x._id)}
            onClose={() => setOpenSMS(false)}
          />
        </BigModal>
      </Stack>
    )
  }

  const columns = [
    {
      field: 'employeeName',
      headerName: 'Employee',
      valueGetter: (value, row) => row.firstName + ' ' + row.lastName,
      renderCell: (params) => (
        <Box display="flex" gap={2} alignItems="center">
          {/* <WarningIcon color="warning" /> */}
          <Avatar src={params.row.photograph} sx={{ width: 32, height: 32 }} />
          <Typography variant="title">
            {`${params.row.firstName} ${params.row.lastName}`}
          </Typography>
        </Box>
      ),
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'pinNumber',
      headerName: 'PIN',
      valueGetter: (value, row) => row.employee.pinNumber,
      renderCell: ({ row }) => row.employee.pinNumber,
      minWidth: 150,
    },
    {
      field: 'clinicalRole',
      headerName: 'Role',
      flex: 0.5,
      minWidth: 150,
      renderCell: (params) => params.row.employee.clinicalRole,
      valueGetter: (value, row) => row.employee.clinicalRole,
    },
    {
      field: 'telephone',
      headerName: 'Telephone',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => params.row.employee.personnelInfo?.phoneNumber1,
      valueGetter: (value, row) => row.employee.personnelInfo?.phoneNumber1,
    },
    {
      field: 'email',
      headerName: 'Email Address',
      flex: 1,
      minWidth: 200,
      valueGetter: (value) => value,
    },
    {
      field: 'linktoIDcheck',
      headerName: 'ID Check URL',
      flex: 1,
      minWidth: 200,
      renderCell: ({ row }) =>
        `${window.location.origin}/id-check/${row.employee._id}`,
      valueGetter: (value, row) =>
        `${window.location.origin}/id-check/${row.employee._id}`,
    },
    {
      field: 'latestDrivingLicenceCheck',
      headerName: 'Latest Driving Licence Check',
      flex: 1,
      minWidth: 200,
      valueGetter: (value, row) =>
        row.employee?.latestDrivingLicenceCheck?.date
          ? moment(row.employee.latestDrivingLicenceCheck.date).format(
              'DD MMM YYYY',
            )
          : '',
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 200,
      valueGetter: (value, row) => row.employee.status,
      renderCell: ({ row }) => row.employee.status,
    },
    {
      field: 'lastActive',
      headerName: 'Last Active',
      minWidth: 200,
      renderCell: ({ row }) => {
        if (row.lastActive) {
          const onlineNow = moment(row.lastActive).isAfter(
            moment().subtract(3, 'minutes'),
          )
          const fromNow = moment(row.lastActive).fromNow()
          if (onlineNow) {
            return (
              <Stack gap={1} direction="row" alignItems="center">
                <>Online Now</>
                <FiberManualRecordIcon color="success" />
              </Stack>
            )
          } else {
            return fromNow
          }
        } else {
          return 'Never'
        }
      },
      valueGetter: (value, row) => row.lastActive,
    },
    {
      field: 'canLogin',
      headerName: 'Login',
      minWidth: 150,
      renderCell: (params) =>
        params.row.security.canLogIn ? (
          <DoneIcon color="success" />
        ) : (
          <DoDisturbIcon color="error" />
        ),
      valueGetter: (value, row) => row.security?.archived,
      flex: 0.5,
      align: 'center',
      headerAlign: 'center',
    },
  ]

  return (
    <Box
      spacing={1}
      display="flex"
      flexDirection="column"
      gap={2}
      component={Paper}
      padding={1}
    >
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', lg: 'center' }}
        spacing={2}
      >
        <Typography variant="title2">Employees</Typography>

        {/* Search Bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            background: 'white',
            borderRadius: 3,
            padding: '4px 12px',
            border: '1px solid #dddddd',
            width: { xs: '100%', md: '350px' },
          }}
        >
          <SearchIcon opacity={0.6} />
          <TextField
            variant="standard"
            placeholder="Search employees..."
            fullWidth
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              disableUnderline: true,
            }}
          />
          {searchInput && (
            <IconButton size="small" onClick={() => setSearchInput('')}>
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Stack>
      <Box>{dataLoading && <LinearProgress />}</Box>
      <CustomDataGrid
        columns={columns}
        checkboxSelection
        onRowSelectionModelChange={(newSelectionModel) =>
          setSelectedEmployeesModel(newSelectionModel)
        }
        rows={employees}
        getRowId={(params) => params._id}
        onRowClick={(params) => navigate(params.row._id)}
        initialState={{
          sorting: {
            sortModel: [{ field: 'employeeName', sort: 'asc' }],
          },
          columns: {
            columnVisibilityModel: {
              linktoIDcheck: false,
              canLogin: false,
              status: false,
              latestDrivingLicenceCheck: false,
            },
          },
        }}
        sx={{
          '.MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
          },
        }}
        slots={{
          toolbar: CustomToolbar,
        }}
        slotProps={{
          toolbar: {
            selectedRows: selectedEmployees,
            csvOptions: { allColumns: true },
          },
        }}
      />
    </Box>
  )
}

export default Employees
